import { getServiceUrl } from '../api.config';
import { ResponseWithBody } from '../types';
import { ApiClientOptions } from '../types/api';
import { DisplayGroup } from '../types/field';
import ApiClient from '../utils/ApiClient';
import qs from '../utils/query';
import {
  isBoolean,
  isNotEmptyArray,
  isPositiveInteger,
  isString,
} from '../utils/validators';

export default class DisplayGroupApi extends ApiClient {
  public constructor(options?: ApiClientOptions) {
    super(getServiceUrl('core'), options);
  }

  public DisplayGroupList(
    queries: {
      contentOwnerId?: number;
      kindId?: number;
      isConsumerUnit?: boolean;
      isDisplayUnit?: boolean;
      typePackagingId?: number;
      isRetailer?: boolean;
      isReadOnly?: boolean;
      noRestriction?: boolean;
      identityBlock?: boolean;
      targetMarketIds?: number[];
      [key: string]: any;
    } = {},
    useCache?: boolean,
  ) {
    const query = '/core/v3/displaygroups';
    const {
      contentOwnerId,
      kindId,
      isConsumerUnit,
      isDisplayUnit,
      typePackagingId,
      isRetailer,
      isReadOnly,
      noRestriction,
      identityBlock,
      targetMarketIds,
      ...additionalQueries
    } = queries;
    const parameters: {
      [key: string]: any;
    } = { ...additionalQueries };

    if (isPositiveInteger(queries.contentOwnerId)) {
      parameters.organization_id = queries.contentOwnerId;
    }
    if (isPositiveInteger(queries.kindId)) {
      parameters.kind_id = queries.kindId;
    }
    if (isBoolean(queries.isConsumerUnit)) {
      parameters.is_consumer_unit = queries.isConsumerUnit ? 1 : 0;
    }
    if (isBoolean(queries.isDisplayUnit)) {
      parameters.is_display_unit = queries.isDisplayUnit ? 1 : 0;
    }
    if (isPositiveInteger(queries.typePackagingId)) {
      parameters.type_packaging_id = queries.typePackagingId;
    }
    if (isBoolean(queries.isRetailer)) {
      parameters.is_retailer = queries.isRetailer ? 1 : 0;
    }
    if (isBoolean(queries.isReadOnly) && queries.isReadOnly) {
      parameters.is_read_only = 1;
    }
    if (isBoolean(queries.noRestriction) && queries.noRestriction) {
      parameters.no_restriction = 1;
    }
    if (isBoolean(queries.identityBlock)) {
      parameters.identity_block = queries.identityBlock ? 1 : 0;
    }
    if (isNotEmptyArray(queries.targetMarketIds)) {
      parameters.targetmarket_ids = queries.targetMarketIds?.join();
    }

    return this.get<DisplayGroup[], ResponseWithBody<DisplayGroup[]>>(
      query,
      qs.stringify(parameters, true),
      useCache,
    );
  }

  public DisplayGroupListFlat(
    queries: {
      search?: string;
    } = {},
  ) {
    const query = '/core/v3/displaygroups/flat';

    let parameters = '?alk=1';
    if (isString(queries.search)) {
      parameters += `&search=${queries.search}`;
    }

    return this.get(query, parameters);
  }

  public DisplayGroupBulkEdit() {
    const query = '/core/v3/displaygroups/bulkedit';
    return this.get(query);
  }

  public DisplayGroupIsMadeOf() {
    const query = '/core/v3/displaygroups/isMadeOf';
    return this.get(query);
  }

  public DisplayGroupSharingUnitList(
    targetOrganizationId: number,
    type?: string,
  ) {
    const query = '/core/v3/displaygroups/sharingunit';
    const params: {
      target_organization_id?: number;
      type?: string;
    } = {};

    if (isPositiveInteger(targetOrganizationId)) {
      params.target_organization_id = targetOrganizationId;
    }

    if (type) {
      params.type = type;
    }

    return this.get(query, qs.stringify(params, true));
  }

  public DisplayGroupEntityTypeList(
    targetOrganizationId: number,
    type: string,
    viewAsType?: string,
  ) {
    const query = `/core/v3/displaygroups/${type}`;
    const params: {
      target_organization_id?: number;
      view_as_entity_type?: string;
    } = {};

    if (isPositiveInteger(targetOrganizationId)) {
      params.target_organization_id = targetOrganizationId;
    }
    if (isString(viewAsType)) {
      params.view_as_entity_type = viewAsType;
    }

    return this.get(query, qs.stringify(params, true));
  }

  public DisplayGroupOrganizationList(targetOrganizationId: number) {
    const query = '/core/v3/displaygroups/organization';
    const params: {
      target_organization_id?: number;
    } = {};

    if (isPositiveInteger(targetOrganizationId)) {
      params.target_organization_id = targetOrganizationId;
    }

    return this.get(query, qs.stringify(params, true));
  }

  public listAdmin() {
    const query = '/core/v3/displaygroups/admin';
    return this.get(query);
  }

  public create(data: any) {
    return this.post('/core/v3/displaygroups', data);
  }

  public update(displayGroupId: number, data: any) {
    const query = `/core/v3/displaygroups/${displayGroupId}`;
    return this.put(query, data);
  }

  public updateFields(displayGroupId: number, fields: any) {
    const query = `/core/v3/displaygroups/${displayGroupId}/fields`;
    return this.put(query, { data: fields });
  }

  public getUserRecipientsDisplayGroups(
    organizationId: number,
    userId: number,
  ) {
    return this.get<DisplayGroup[], ResponseWithBody<DisplayGroup[]>>(
      '/core/v3/displaygroups/for_user_recipients',
      `?organization_id=${organizationId}&user_id=${userId}`,
    );
  }
}
